import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { userSettingsAPI } from '../utils/supabase'

// Async thunks
export const fetchUserSettings = createAsyncThunk(
  'userSettings/fetch',
  async (userId, { rejectWithValue }) => {
    try {
      const { data: userSettings, error } = await userSettingsAPI.getUserSettings(userId)
      
      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        throw error
      }
      
      if (userSettings) {
        // Transform database snake_case to camelCase
        return {
          timezone: userSettings.timezone,
          dateFormat: userSettings.date_format,
          timeFormat: userSettings.time_format,
          themePreference: userSettings.theme_preference,
          showDailyFocus: userSettings.show_daily_focus,
          showInbox: userSettings.show_inbox,
          showCritical: userSettings.show_critical,
          showOverdue: userSettings.show_overdue,
          showFlagged: userSettings.show_flagged,
          showDueSoon: userSettings.show_due_soon,
          showStuck: userSettings.show_stuck,
          showQuickWin: userSettings.show_quick_win,
          attentionThresholds: userSettings.attention_thresholds,
          savedKanbanFilters: userSettings.saved_kanban_filters || [],
          excludeWeekendsFromMetrics: userSettings.exclude_weekends_from_metrics ?? false,
          selectedHolidayCalendars: userSettings.selected_holiday_calendars || [],
          customExcludedDates: userSettings.custom_excluded_dates || [],
          onboarding_completed: userSettings.onboarding_completed,
          realtime_enabled: userSettings.realtime_enabled ?? false,
          uiScale: userSettings.ui_scale || 'roomy'
        }
      } else {
        // Initialize settings with detected timezone and default attention sidebar settings if none exist
        const { getDetectedTimezone } = await import('../utils/timezoneUtils')
        const detectedTimezone = getDetectedTimezone()
        const defaultSettings = {
          timezone: detectedTimezone,
          // Attention sidebar visibility settings - all default to true
          showDailyFocus: true,
          showInbox: true,
          showCritical: true,
          showOverdue: true,
          showFlagged: true,
          showDueSoon: true,
          showStuck: true,
          showQuickWin: true,
          // Theme preference - default to system
          themePreference: 'system',
          // UI scale - default to roomy
          uiScale: 'roomy',
          // Saved Kanban filters - default to empty array
          savedKanbanFilters: [],
          // Performance metrics settings
          excludeWeekendsFromMetrics: false,
          selectedHolidayCalendars: [],
          customExcludedDates: [],
          // Onboarding status - default to false for new users
          onboarding_completed: false,
        }
        await userSettingsAPI.initializeUserSettings(userId, detectedTimezone, defaultSettings)
        return defaultSettings
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const updateUserSettings = createAsyncThunk(
  'userSettings/update',
  async ({ userId, settings }, { rejectWithValue, getState }) => {
    try {
      // Get current settings from Redux state to pass to API
      // This eliminates unnecessary database GET request
      const currentSettings = getState().userSettings.settings

      const { error } = await userSettingsAPI.upsertUserSettings(userId, settings, currentSettings)
      if (error) throw error
      return settings
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

const initialState = {
  settings: null,
  loading: false,
  error: null,
  initialized: false
}

const userSettingsSlice = createSlice({
  name: 'userSettings',
  initialState,
  reducers: {
    setSettings: (state, action) => {
      state.settings = action.payload
      state.initialized = true
    },
    clearUserSettings: (state) => {
      state.settings = null
      state.initialized = false
      state.error = null
    },
    clearError: (state) => {
      state.error = null
    },
    setOnboardingCompleted: (state) => {
      if (state.settings) {
        state.settings.onboarding_completed = true
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch User Settings
      .addCase(fetchUserSettings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUserSettings.fulfilled, (state, action) => {
        state.loading = false
        state.settings = action.payload
        state.initialized = true
      })
      .addCase(fetchUserSettings.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.initialized = true
      })

      // Update User Settings
      .addCase(updateUserSettings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateUserSettings.fulfilled, (state, action) => {
        state.loading = false
        state.settings = { ...state.settings, ...action.payload }
      })
      .addCase(updateUserSettings.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const { setSettings, clearUserSettings, clearError, setOnboardingCompleted } = userSettingsSlice.actions

// Selectors
export const selectSavedKanbanFilters = (state) => state.userSettings.settings?.savedKanbanFilters || []

export default userSettingsSlice.reducer
